import * as cookie from "cookie";
import { createServerFn } from "@tanstack/react-start";
import { Result } from "better-result";
import { z } from "zod/v4";
import { client } from "#/modules/util.fetch-client/fetch-client.server";
import { toResult, UnknownApiError } from "#/modules/util.auth/api-errors";
import { getUserInfo, verifyPasskeyLogin } from "#/modules/util.auth/api.auth.server";
import { completeLogin, type LoginRedirect } from "#/modules/util.auth/authenticate.server";
import { getPasskeySession } from "#/modules/util.auth/cookie.passkey.server";
import { authMiddleware } from "#/modules/util.auth/middleware.auth";
import { translateApiError } from "#/modules/util.errors/translate";
import { setSentryUser } from "#/modules/util.instrumentation/sentry";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";

// ---------------------------------------------------------------------------
// Return types (all concrete + serializable — no `unknown`)
// ---------------------------------------------------------------------------

// The DOM extension inputs allow BufferSources, which server functions cannot serialize. The
// backend sends plain JSON, so any extensions still pass through untouched at runtime.
export type PasskeyRequestOptions = Omit<PublicKeyCredentialRequestOptionsJSON, "extensions">;
type PasskeyCreationOptions = Omit<PublicKeyCredentialCreationOptionsJSON, "extensions">;

export type PasskeyRequestOptionsResult =
  | { options: PasskeyRequestOptions }
  | { formError: string };

type PasskeyLoginVerifyResult = { redirect: LoginRedirect } | { formError: string };

// ---------------------------------------------------------------------------
// Login ceremony — unauthenticated
// ---------------------------------------------------------------------------

/**
 * Step 1 of the login ceremony.
 * Calls `GET /auth/login/passkey/request-options`, reads the `Identity.TwoFactorUserId`
 * cookie from the response headers, stores it in the short-lived passkey session, and
 * returns the `PublicKeyCredentialRequestOptionsJSON` to the client.
 */
export const passkeyLoginRequestOptionsFn = createServerFn({ method: "POST" }).handler(
  async (): Promise<PasskeyRequestOptionsResult> => {
    const raw = await client.GET("/auth/login/passkey/request-options", { parseAs: "text" });
    const optionsResult = toResult(raw);

    if (Result.isError(optionsResult)) {
      return { formError: translateApiError(optionsResult.error) };
    }
    const requestOptions = optionsResult.value;
    const response = raw.response;

    // Extract Microsoft Identity cookie and stash it in the short-lived passkey session.
    // `getSetCookie()` (not `get("set-cookie")` + `parseCookie`, which mis-handles multiple
    // Set-Cookie headers) returns each Set-Cookie header separately; parse each to find the one
    // we need.
    const cookieValue = response.headers
      .getSetCookie()
      .map((header) => cookie.parseSetCookie(header))
      .find((parsed) => parsed.name === "Identity.TwoFactorUserId")?.value;
    if (cookieValue) {
      const passkeySession = await getPasskeySession();
      await passkeySession.update({ cookie: cookieValue });
    }

    // The schema types this 200 body as `content?: never`; it is the WebAuthn request options JSON.
    const options: PasskeyRequestOptions = JSON.parse(requestOptions);
    return { options };
  },
);

/**
 * Step 3 of the login ceremony.
 * Reads the stored `Identity.TwoFactorUserId` from the passkey session, echoes it as a
 * `Cookie` header on the verify call, stores tokens + user in the main auth session, and
 * returns the post-login redirect URL.
 */
export const passkeyLoginVerifyFn = createServerFn({ method: "POST" })
  .validator(z.object({ credentials: z.string().min(1), referer: z.string().optional() }))
  .handler(async ({ data }): Promise<PasskeyLoginVerifyResult> => {
    const passkeySession = await getPasskeySession();
    const cookieValue = passkeySession.data.cookie;

    let extraHeaders: HeadersInit | undefined;
    if (cookieValue) {
      extraHeaders = {
        Cookie: cookie.stringifySetCookie({
          name: "Identity.TwoFactorUserId",
          value: cookieValue,
          path: "/",
        }),
      };
    }

    const authResult = await Result.gen(async function* () {
      const loginResponse = yield* Result.await(verifyPasskeyLogin(data.credentials, extraHeaders));

      if (!loginResponse.accessToken || !loginResponse.refreshToken) {
        return Result.err(new UnknownApiError({ message: "PasskeyTokensMissing" }));
      }

      const userInfo = yield* Result.await(getUserInfo(loginResponse.accessToken));
      return Result.ok({ loginResponse, userInfo });
    });

    if (Result.isError(authResult)) {
      return { formError: translateApiError(authResult.error) };
    }

    const { loginResponse, userInfo } = authResult.value;
    setSentryUser(userInfo.email);

    // completeLogin persists the session AND the post-login return URL (e.g. the invite accept
    // link) — without the referer a school-less invitee dead-ends on the no-school page.
    const redirectResult = await completeLogin({
      tokens: loginResponse,
      user: userInfo,
      referer: data.referer,
    });
    if (Result.isError(redirectResult)) {
      return { formError: translateApiError(redirectResult.error) };
    }

    return { redirect: redirectResult.value };
  });

// ---------------------------------------------------------------------------
// Registration ceremony — authenticated
// ---------------------------------------------------------------------------

/**
 * Step 1 of the registration ceremony.
 * Requires an authenticated session (authMiddleware). Calls
 * `GET /auth/user/passkey/create-options`, stashes the Microsoft Identity cookie,
 * and returns `PublicKeyCredentialCreationOptionsJSON` to the client.
 */
export const passkeyRegisterCreateOptionsFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .handler(async () => {
    const raw = await client.GET("/auth/user/passkey/create-options", { parseAs: "text" });
    const optionsResult = toResult(raw);

    if (Result.isError(optionsResult)) {
      return { formError: translateApiError(optionsResult.error) };
    }
    const createOptions = optionsResult.value;
    const response = raw.response;

    // `getSetCookie()` (not `get("set-cookie")` + `parseCookie`, which mis-handles multiple
    // Set-Cookie headers) returns each Set-Cookie header separately; parse each to find the one
    // we need.
    const cookieValue = response.headers
      .getSetCookie()
      .map((header) => cookie.parseSetCookie(header))
      .find((parsed) => parsed.name === "Identity.TwoFactorUserId")?.value;
    if (cookieValue) {
      const passkeySession = await getPasskeySession();
      await passkeySession.update({ cookie: cookieValue });
    }

    // The schema types this 200 body as `content?: never`; it is the WebAuthn creation options JSON.
    const options: PasskeyCreationOptions = JSON.parse(createOptions);
    return { options };
  });

/**
 * Step 3 of the registration ceremony.
 * Requires an authenticated session (authMiddleware). Echoes the stored Microsoft
 * Identity cookie, sends credentials + name to `POST /auth/user/passkey/verify`.
 */
export const passkeyRegisterVerifyFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ credentials: z.string().min(1), name: z.string().min(1) }))
  .handler(async ({ data }) => {
    const passkeySession = await getPasskeySession();
    const cookieValue = passkeySession.data.cookie;

    let extraHeaders: HeadersInit | undefined;
    if (cookieValue) {
      extraHeaders = {
        Cookie: cookie.stringifySetCookie({
          name: "Identity.TwoFactorUserId",
          value: cookieValue,
          path: "/",
        }),
      };
    }

    const result = toResult(
      await client.POST("/auth/user/passkey/verify", {
        body: { credentials: data.credentials, name: data.name },
        headers: extraHeaders,
      }),
    );

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }
  });

// ---------------------------------------------------------------------------
// Passkey management — authenticated
// ---------------------------------------------------------------------------

export const listPasskeysFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async () => {
    const result = toResult(await client.GET("/auth/user/passkey"));

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }

    return { passkeys: result.value?.passkeys ?? [] };
  });

export const deletePasskeyFn = createServerFn({ method: "POST" })
  .middleware([authMiddleware])
  .validator(z.object({ credentialId: z.string().min(1) }))
  .handler(async ({ data }) => {
    const result = toResult(
      await client.DELETE("/auth/user/passkey", {
        body: { credentialId: data.credentialId },
      }),
    );

    if (Result.isError(result)) {
      return { formError: translateApiError(result.error) };
    }
  });
